import {isEmpty, map} from 'lodash';
import {useCallback, useState} from 'react';
import {useTranslation} from 'react-i18next';

import {agentActionsApi} from 'api';
import {ConfirmModal} from 'components';
import {useApiData, useObservable, useUserSession} from 'hooks';
import {TRANSLATION_KEYS} from 'locale';
import {authDataStore} from 'services';

import {SidebarView} from '../../types';
import {SidebarContent} from '../SidebarContent';

import {AddNote} from './AddNote';
import {NoteItem} from './NoteItem';
import {PrevSessionsNotes} from './PrevSessionNotes';

const T_KEYS = TRANSLATION_KEYS.IN_SESSION_PAGE.NOTES_SIDEBAR;

export const NotesSidebar: React.FC = () => {
    const {t} = useTranslation();

    const [confirmDeleteModalProps, setConfirmDeleteModalProps] = useState<{
        open: boolean;
        noteId?: string;
    }>({open: false});

    const {localUser} = useObservable(authDataStore);
    const {session, sessionParams} = useUserSession();

    const handleConfirmDeleteModalOpen = useCallback((noteId: string) => {
        setConfirmDeleteModalProps({open: true, noteId});
    }, []);

    const handleConfirmDeleteNoteModalClose = useCallback(() => {
        setConfirmDeleteModalProps((prev) => ({...prev, open: false}));
    }, []);

    const {send: deleteNoteFn} = useApiData(agentActionsApi.deleteNote, {onSuccess: handleConfirmDeleteNoteModalClose});

    const deleteSessionNote = useCallback((id: string) => {
        deleteNoteFn(sessionParams, {id});
    }, [sessionParams, deleteNoteFn]);

    const handleCloseDeleteModal = useCallback((confirm: boolean) => {
        if (!confirm) {
            handleConfirmDeleteNoteModalClose();
            return;
        }

        deleteSessionNote(confirmDeleteModalProps.noteId);
    }, [deleteSessionNote, confirmDeleteModalProps, handleConfirmDeleteNoteModalClose]);

    return (
        <SidebarContent view={SidebarView.Notes} header={<div className='tw-ts-h5'>{t(T_KEYS.TITLE)}</div>}>
            <AddNote />
            {isEmpty(session.notes) && <div className='tw-ts-h5 tw-my-4 tw-text-default-dimmed'>{t(T_KEYS.NO_NOTES)}</div>}
            {map(session.notes, (note, index) => (
                <NoteItem
                    key={index}
                    note={note}
                    editable
                    localUserEid={localUser.eid}
                    participants={[session.localParticipant]}
                    onDelete={handleConfirmDeleteModalOpen}
                />
            ))}
            <PrevSessionsNotes localUser={localUser} />
            <ConfirmModal {...confirmDeleteModalProps} title={t(T_KEYS.CONFIRM_DELETE)} onClose={handleCloseDeleteModal} />
        </SidebarContent>
    );
};